import { MercatoFields } from "@/pages/trattative/QuestionarioBox";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Boxes, RefreshCw } from "lucide-react";
import { ProdottoDocumenti } from "@/pages/admin/ProdottoDocumenti";

const TARGET_LABEL = {
  quotatore_rc: "Quotatore RC (nativo)",
  quotatore_mrv: "Quotatore MRV (nativo)",
  mup: "MUP (precontrattuale)",
};
const empty = { code: "", nome: "", famiglia: "", compagnia: "", aliases: [], routing_target: "mup", motori: [], soluzioni: [], ordine: 99, attivo: true, note: "", portale_url: "", documenti: {}, giorni_recesso: 14, mercato: {}, non_vendibile_distanza: false };

export default function CatalogoTab() {
  const [prodotti, setProdotti] = useState([]);
  const [coverage, setCoverage] = useState({ prodotti: [], orphans: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [aliasStr, setAliasStr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/catalogo/prodotti"); setProdotti(data); } catch { /* noop */ }
    try { const { data } = await api.get("/catalogo/coverage"); setCoverage(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const covOf = (code) => (coverage.prodotti || []).find((c) => c.code === code) || {};

  const openNew = () => { setEditing(null); setForm(empty); setAliasStr(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setAliasStr((p.aliases || []).join(", ")); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, aliases: aliasStr.split(",").map((a) => a.trim()).filter(Boolean) };
      if (editing) { await api.put(`/catalogo/prodotti/${editing.code}`, payload); toast.success("Prodotto aggiornato"); }
      else { await api.post("/catalogo/prodotti", payload); toast.success("Prodotto aggiunto"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/catalogo/prodotti/${p.code}`); toast.success("Prodotto eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncPortale = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/portale/sync-catalogo");
      if (!data.ok) {
        toast.error(data.reason === "not_configured" ? "Collegamento al Portale non attivo (configuralo nella card Prodotti & Tariffe Portale)" : `Errore Portale: ${data.error || data.reason}`);
      } else {
        toast.success(`Sincronizzati ${data.aggiornati.length} prodotti dal Portale${data.non_trovati.length ? ` · ${data.non_trovati.length} non in catalogo` : ""}`);
        load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const addOrphan = (o) => {
    setEditing(null);
    setForm({ ...empty, nome: o.name, famiglia: o.family || "", routing_target: "mup" });
    setAliasStr(o.name.toLowerCase());
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-slate-900">Catalogo Prodotti Unico</h3>
          <p className="text-sm text-slate-500">{prodotti.length} prodotti — fonte di verità letta dall'Assietica AI</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="sync-portale-btn" onClick={syncPortale} disabled={syncing} variant="outline" className="border-slate-300">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizzo…" : "Sincronizza dal Portale"}
          </Button>
          <Button data-testid="add-prodotto-btn" onClick={openNew} className="brand-gradient text-white border-0">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi prodotto
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {prodotti.map((p) => (
          <div key={p.code} data-testid={`catalogo-prod-${p.code}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Boxes className="h-5 w-5 text-indigo-600" />
                <h4 className="font-heading font-medium text-slate-900">{p.nome}</h4>
                {!p.attivo && <Badge variant="outline" className="text-slate-400">disattivo</Badge>}
                <Badge variant="outline" className="text-indigo-600 border-indigo-200">{TARGET_LABEL[p.routing_target] || p.routing_target}</Badge>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openEdit(p)} data-testid={`edit-prod-${p.code}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" data-testid={`delete-prod-${p.code}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare {p.nome}?</AlertDialogTitle>
                      <AlertDialogDescription>Il prodotto verrà rimosso dal catalogo unico.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p)} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-2">{p.famiglia}{p.compagnia ? ` · ${p.compagnia}` : ""}</p>
            {(p.engine_ref || p.versione) && (
              <div className="mb-2" data-testid={`catalogo-portale-${p.code}`}>
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium bg-teal-100 text-teal-700" title={`Sincronizzato dal Portale${p.portale_sync_at ? ` il ${new Date(p.portale_sync_at).toLocaleDateString("it-IT")}` : ""}`}>
                  Portale: {p.engine_ref || "—"}{p.versione ? ` · v.${p.versione}` : ""}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-1 mb-2" data-testid={`catalogo-cov-${p.code}`}>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${covOf(p.code).quotatore_nativo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {covOf(p.code).quotatore_nativo ? "Quotatore nativo ✓" : "Quotatore nativo —"}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${(covOf(p.code).mup || []).length ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`} title={(covOf(p.code).mup || []).join(", ")}>
                {(covOf(p.code).mup || []).length ? `MUP ✓ (${covOf(p.code).mup.length})` : "MUP non collegato"}
              </span>
            </div>
            {(p.motori || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {p.motori.map((m, i) => (
                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${m.tipo === "portale_partner" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} title={m.nota || ""}>
                    {m.tipo === "portale_partner" ? "Portale Partner" : m.tipo === "mup" ? "MUP" : m.tipo === "quotatore_rc" ? "Quotatore RC" : m.tipo === "quotatore_mrv" ? "Quotatore MRV" : m.tipo}
                  </span>
                ))}
              </div>
            )}
            {(p.soluzioni || []).length > 0 && (
              <p className="text-xs text-slate-600 mb-1"><span className="font-medium">Soluzioni:</span> {p.soluzioni.map((s) => s.nome).join(", ")}</p>
            )}
            <p className="text-xs text-slate-400 line-clamp-1">alias: {(p.aliases || []).join(", ")}</p>
          </div>
        ))}
      </div>

      {(coverage.orphans || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="catalogo-orphans">
          <h4 className="font-heading font-medium text-amber-900 mb-1">Prodotti MUP non ancora nel catalogo unico</h4>
          <p className="text-sm text-amber-700 mb-3">Questi prodotti esistono in MUP ma non corrispondono ad alcun prodotto canonico. Aggiungili al catalogo per renderli riconoscibili dall'AI.</p>
          <div className="flex flex-wrap gap-2">
            {coverage.orphans.map((o) => (
              <button key={o.name} data-testid={`orphan-${o.name}`} onClick={() => addOrphan(o)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 transition-colors">
                <Plus className="h-3 w-3" /> {o.name} <span className="text-amber-400">· {o.family}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifica prodotto" : "Aggiungi prodotto"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nome</Label><Input data-testid="prod-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Famiglia</Label><Input data-testid="prod-famiglia-input" value={form.famiglia} onChange={(e) => setForm({ ...form, famiglia: e.target.value })} /></div>
              <div className="space-y-2"><Label>Compagnia</Label><Input data-testid="prod-compagnia-input" value={form.compagnia} onChange={(e) => setForm({ ...form, compagnia: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Dove l'AI apre la pratica (oggi)</Label>
              <Select value={form.routing_target} onValueChange={(v) => setForm({ ...form, routing_target: v })}>
                <SelectTrigger data-testid="prod-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quotatore_rc">Quotatore RC (nativo)</SelectItem>
                  <SelectItem value="quotatore_mrv">Quotatore MRV (nativo)</SelectItem>
                  <SelectItem value="mup">MUP (precontrattuale)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alias / sinonimi (separati da virgola)</Label>
              <Textarea data-testid="prod-aliases-input" value={aliasStr} onChange={(e) => setAliasStr(e.target.value)} placeholder="es. economia solidale, rc terzo settore" rows={2} />
              <p className="text-xs text-slate-400">Usati dall'AI per riconoscere il prodotto dal testo della polizza.</p>
            </div>
            <div className="space-y-2">
              <Label>Indirizzo preventivatore sul Portale Partner</Label>
              <Input data-testid="prod-portale-url-input" value={form.portale_url || ""} onChange={(e) => setForm({ ...form, portale_url: e.target.value })} placeholder="es. https://www.portalepartner.it/preventivo/cat-nat?piva={partita_iva}" />
              <p className="text-xs text-slate-400">Segnaposto utilizzabili: {"{partita_iva} {ragione_sociale} {codice_fiscale} {comune} {provincia} {ateco}"}. Senza segnaposto i dati vengono aggiunti in coda all'indirizzo.</p>
            </div>
            <div className="space-y-2">
              <Label>Giorni per il recesso (vendita a distanza)</Label>
              <Input type="number" min={14} max={60} data-testid="prod-giorni-recesso-input" value={form.giorni_recesso ?? 14} onChange={(e) => setForm({ ...form, giorni_recesso: Number(e.target.value) || 14 })} className="w-28" />
              <p className="text-xs text-slate-500">Di norma 14 giorni per RCA e Rami Elementari.</p>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!form.non_vendibile_distanza} onChange={(e) => setForm({ ...form, non_vendibile_distanza: e.target.checked })} data-testid="prod-non-vendibile-distanza" /> Non vendibile a distanza</label>
              <p className="text-xs text-slate-500">I prodotti della famiglia «Vita» non sono mai vendibili interamente a distanza.</p>
            </div>
            <MercatoFields form={form} setForm={setForm} Label={Label} />
            <ProdottoDocumenti code={editing?.code} documenti={form.documenti || {}} onChange={(documenti) => setForm({ ...form, documenti })} />
            <div className="space-y-2"><Label>Note</Label><Textarea data-testid="prod-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={!!form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} data-testid="prod-attivo-switch" />
              <Label className="text-sm">Prodotto attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-prod-btn" onClick={save} disabled={saving || !form.nome} className="brand-gradient text-white border-0">
              {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
